/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BEAN WARS - "CLOAKS VS MIGOS" GAME ARCHITECTURE DOCUMENTATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This file serves as the main application controller for Bean Wars, a Space
 * Invaders-inspired arcade shooter featuring Nakamigos characters.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 📁 FILE STRUCTURE & RESPONSIBILITIES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ┌─ ENTRY POINT & UI ────────────────────────────────────────────────────┐
 * │                                                                         │
 * │ • index.html                                                           │
 * │   → Main HTML document with ALL screen layouts (home, character        │
 * │     select, game, pause, game over, victory, leaderboard)              │
 * │   → Contains all CSS styling and responsive design                     │
 * │   → Canvas element for game rendering (360x540)                        │
 * │   → Modal overlays (email, how-to-play, pause menu)                    │
 * │                                                                         │
 * │ • src/main.js (THIS FILE)                                              │
 * │   → BeanWarsApp class - main application controller                    │
 * │   → Screen navigation and state management                             │
 * │   → Character selection UI rendering                                   │
 * │   → Game initialization and game loop coordination                     │
 * │   → End-of-run score recording and leaderboard display                 │
 * │   → Event listener setup for all UI interactions                       │
 * │                                                                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ CORE GAME SYSTEMS ───────────────────────────────────────────────────┐
 * │                                                                         │
 * │ • src/game/GameEngine.js                                               │
 * │   → Main game loop (requestAnimationFrame)                             │
 * │   → Entity management (player, enemies, bullets, powerups)             │
 * │   → Collision detection and damage handling                            │
 * │   → Wave/level progression logic                                       │
 * │   → Boss spawning and Beeple final boss                                │
 * │   → Particle system updates                                            │
 * │   → Scrolling star background                                          │
 * │   → HUD updates (score, hearts, level)                                 │
 * │                                                                         │
 * │ • src/game/Player.js                                                   │
 * │   → Player entity class                                                │
 * │   → Movement with deltaTime-based physics                              │
 * │   → Different movement modes (Cloaks: horizontal wrap,                 │
 * │     Migos: full vertical)                                              │
 * │   → Shooting mechanics with character-specific fire rates              │
 * │   → Special ability charge and activation                              │
 * │   → Trail particle generation when moving                              │
 * │   → Invincibility frames after taking damage                           │
 * │   → Visual glow effects for special charge/active                      │
 * │                                                                         │
 * │ • src/game/Enemy.js                                                    │
 * │   → Enemy class with multiple behavior patterns                        │
 * │   → Boss class with 5 movement patterns and 5 attack patterns          │
 * │   → HP system with visual health bars for bosses                       │
 * │   → Deterministic firing patterns (staggered, aimed, spread)           │
 * │   → Boss movement: horizontal bounce, rush, waves, circular,           │
 * │     aggressive charge                                                  │
 * │   → Boss attacks: spiral, spinning projectiles, burst spread,          │
 * │     triple spread, aimed shots                                         │
 * │                                                                         │
 * │ • src/game/Bullet.js                                                   │
 * │   → Bullet entity for both player and enemy projectiles                │
 * │   → Character-specific projectile sprites                              │
 * │   → Velocity-based movement with deltaTime scaling                     │
 * │   → Glow effects for different bullet types                            │
 * │   → Image caching for performance                                      │
 * │                                                                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ SPECIAL ABILITIES SYSTEM ────────────────────────────────────────────┐
 * │                                                                         │
 * │ • src/game/SpecialAbilities.js                                         │
 * │   → Registry of 12 unique character special abilities                  │
 * │   → Each ability has activate(), update(), render(), deactivate()      │
 * │                                                                         │
 * │   CLOAKS ABILITIES:                                                    │
 * │   • Blood Fan Barrage (Bat) - 5-way red laser spread                   │
 * │   • One Slice (Scythe) - Screen-wide slicing wave                      │
 * │   • Time Bubble (Archer-Eyepatch) - Slow enemies and bullets           │
 * │   • Steam Overclock (Cheetah) - Rapid fire + steam trails              │
 * │   • Crema Beam (Archer-Fire) - Continuous piercing beam                │
 * │   • Café Refill (Forest Wolf) - Heal + 2x bean points                  │
 * │                                                                         │
 * │   MIGOS ABILITIES:                                                     │
 * │   • 3D Glitch (White Shag) - Hologram clones that fire                 │
 * │   • Raid Overdrive (Pigtails) - 2x fire rate + dash speed              │
 * │   • Chain Mines (Ginger) - 2x stats + invulnerable shield              │
 * │   • Croc Shield (Croc) - Invincibility + contact damage                │
 * │   • Frost Flurry (Snowman) - Triple-shot snowflake spread              │
 * │   • Chomp Dash (Frog) - 2.5x speed + heal on kill                      │
 * │                                                                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ GAME CONFIGURATION ──────────────────────────────────────────────────┐
 * │                                                                         │
 * │ • src/config/constants.js                                              │
 * │   → Canvas dimensions (360x540)                                        │
 * │   → Player stats defaults (speed, bullet speed, lives)                 │
 * │   → 12 character definitions (6 Cloaks, 6 Migos) with:                 │
 * │     - Unique sprites and projectiles                                   │
 * │     - Individual stats (speed, fire rate, bullet speed)                │
 * │     - Special ability IDs and descriptions                             │
 * │     - Movement perks (horizontal wrap vs full vertical)                │
 * │   → 10 enemy types (Fried Egg, Bacon, Bagel, Cappuccino, etc.)        │
 * │   → 5 level configurations with boss waves                             │
 * │   → Powerup types (Bean, Heart)                                        │
 * │   → Boss HP multipliers per level                                      │
 * │                                                                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─ UTILITIES & SUPPORT SYSTEMS ─────────────────────────────────────────┐
 * │                                                                         │
 * │ • src/utils/input.js                                                   │
 * │   → InputManager class for keyboard and touch controls                 │
 * │   → Keyboard: WASD/Arrow keys, Space to fire, E for special            │
 * │   → Virtual joystick with double-tap for special                       │
 * │   → Auto-fire when moving (arcade-style)                               │
 * │                                                                         │
 * │ • src/utils/audio.js                                                   │
 * │   → AudioManager singleton for sound effects and music                 │
 * │   → Side-specific music tracks (cloaks1, cloaks2, migos1, migos2)      │
 * │   → Boss battle music                                                  │
 * │   → Sound effects: shoot, hit, powerup, explosion, game over           │
 * │   → Mute toggle functionality                                          │
 * │   → Audio unlock for mobile browsers                                   │
 * │                                                                         │
 * │ • src/utils/leaderboard.js                                             │
 * │   → Leaderboard manager for score persistence                          │
 * │   → Supabase integration for cloud storage                             │
 * │   → Score submission with player name, email (optional)                │
 * │   → Filtering by side (Cloaks/Migos/All)                               │
 * │   → Top 100 scores display                                             │
 * │                                                                         │
 * │ • src/config/googleSheets.js                                           │
 * │   → Google Sheets API integration                                      │
 * │   → Leaderboard cloud sync configuration                               │
 * │                                                                         │
 * │ • src/game/Powerup.js                                                  │
 * │   → Powerup entity class (Bean, Heart)                                 │
 * │   → Falling animation and collection                                   │
 * │                                                                         │
 * │ • src/game/Particle.js                                                 │
 * │   → Particle class for explosion effects                               │
 * │   → createExplosion() helper function                                  │
 * │   → Used for hits, kills, trails, and special effects                  │
 * │                                                                         │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🎮 GAME FLOW
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 1. HOME SCREEN (index.html #screen-home, managed by this file)
 *    → Player enters name
 *    → Chooses side: Cloaks or Migos
 *    → Can view leaderboard or toggle mute
 *
 * 2. CHARACTER SELECT (index.html #screen-characters, managed by this file)
 *    → Shows 6 characters for chosen side
 *    → Displays character stats, special ability, projectile preview
 *    → "How to Play" shown before selection
 *    → Side-specific movement perk descriptions
 *
 * 3. GAMEPLAY (index.html #screen-game, managed by GameEngine.js)
 *    → Canvas-based rendering (360x540)
 *    → 3-second countdown before each wave
 *    → Player fights through waves of enemies
 *    → ONE wave per level, then boss fight
 *    → 5 levels total, then Beeple final boss
 *    → Special meter charges by 0.25 per frame (slow charge)
 *    → Hearts system (5 hearts, always visible)
 *    → HUD: Score, Hearts, Level, Horizontal Special Meter
 *    → Virtual joystick on bottom (slimmed down)
 *
 * 4. END SCREEN (index.html #screen-gameover or #screen-victory)
 *    → Character portrait on LEFT, stats on RIGHT
 *    → Stats: Side score format ("Cloaks: 1234"), time, specials, hearts
 *    → Record Score button triggers email modal
 *    → Email modal: optional email input with validation
 *    → Two buttons: "Submit & Join Leaderboard" and "Back to Main Menu"
 *
 * 5. LEADERBOARD (index.html #screen-leaderboard, managed by this file)
 *    → Top 100 scores from Supabase
 *    → Filter by All/Cloaks/Migos
 *    → Shows rank, name, character, side, score
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔧 KEY MECHANICS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * • Movement: deltaTime-based with moveScale (0.0007) for arcade feel
 * • Cloaks: Horizontal screen wrap, restricted vertical (bottom 220px)
 * • Migos: Full vertical range (60px to bottom), no horizontal wrap
 * • Auto-fire: Bullets fire automatically when moving (Space Invaders style)
 * • Special Charge: Fills slowly over time (0.25/frame), activates at 100%
 * • Boss Patterns: 5 deterministic movement + 5 deterministic attack patterns
 * • Bullet Clearing: All enemy bullets cleared on wave transitions
 * • Particle Trails: Player leaves colored particles when moving
 * • Powerups: 30% spawn chance on kill (60% beans, 40% hearts)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 📝 IMPORTANT NOTES FOR AI ASSISTANTS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * • This file (main.js) handles ALL screen transitions and UI interactions
 * • GameEngine.js handles the actual gameplay loop and entity management
 * • Constants.js is the single source of truth for game balance
 * • Special abilities are modular and defined in SpecialAbilities.js
 * • Boss patterns use smooth easing for intentional, predictable movement
 * • All sprites and projectiles loaded from CDN URLs
 * • Email is optional for leaderboard - can skip and still view scores
 * • Music changes per side and alternates between tracks per level
 *
 * Last Updated: 2025-12-04
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { GameEngine } from './game/GameEngine.js';
import { InputManager } from './utils/input.js';
import { audioManager } from './utils/audio.js';
import { leaderboardManager } from './utils/leaderboard.js';
import { CHARACTERS, GAME_STATES, BEAN_POINTS } from './config/constants.js';
import { BLACKBIRD_CAFE_URL } from './config/googleSheets.js';

/// Leaderboard image mapping: original character images → leaderboard-specific images with backgrounds
const LEADERBOARD_IMAGE_MAP = {
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e19872724aa3ba4ad_N_9770.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fcd62d13e75c5d526_N_9770%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ec00212b1b303ce07_N_19130.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4faee3726ca69abf46_N_19130%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee043e99baed366a1_n_17763.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fbdd7184b657c0f77_n_17763%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e7e99d28670266a13_N_2818.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fbf7a8fb585fc23f0_N_2818%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24eea51a7384061698b_c_19431.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fedfae48bfb35d751_c_19431%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e962d34eb4472da90_c_11292.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f9b06abd73f9538c3_c_11292%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24edcb42d79f6aef702_n_2869.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9b2f46f39f77faade70b_2869%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e0afc9c0f75ff097c_c_16564.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f0dd4c5428bf07dc8_c_16564%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee1e3bdf8eb1d3d97_c_7130.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f934bb3527e18427a_c_7130%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24e3333bf67710ef8d0_c_151.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f3cf08f8c7a7f8df8_c_151%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24eafcfe74681d63ca1_n_6672.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f73ee5ad0384b0526_n_6672%20Small.jpeg',
  'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24ee3a8c903c6bf3ab0_c_1887.png': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9bc58e28567b381f1ce4_1887%20Small.jpeg'
};

// Character ID to leaderboard image mapping (fallback when URL matching fails)
// Keys are lowercase — lookup normalizes to lowercase for case-insensitive matching
const CHARACTER_ID_IMAGE_MAP = {
  // Cloaks
  'c_151': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f3cf08f8c7a7f8df8_c_151%20Small.jpeg',
  'c_7130': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f934bb3527e18427a_c_7130%20Small.jpeg',
  'c_11292': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f9b06abd73f9538c3_c_11292%20Small.jpeg',
  'c_16564': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f0dd4c5428bf07dc8_c_16564%20Small.jpeg',
  'c_19431': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fedfae48bfb35d751_c_19431%20Small.jpeg',
  'c_1887': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9bc58e28567b381f1ce4_1887%20Small.jpeg',
  // Migos
  'n_17763': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fbdd7184b657c0f77_n_17763%20Small.jpeg',
  'n_2818': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fbf7a8fb585fc23f0_N_2818%20Small.jpeg',
  'n_6672': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4f73ee5ad0384b0526_n_6672%20Small.jpeg',
  'n_9770': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4fcd62d13e75c5d526_N_9770%20Small.jpeg',
  'n_19130': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9a4faee3726ca69abf46_N_19130%20Small.jpeg',
  'n_2869': 'https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/694e9b2f46f39f77faade70b_2869%20Small.jpeg',
};

// ═══════════════════════════════════════════════════════════════════════════
// WEEKLY LEADERBOARD SYSTEM
// ═══════════════════════════════════════════════════════════════════════════
// Weeks run Monday 00:00 UTC → Sunday 23:59 UTC.
// Each Monday the "Prizes" tab resets to show only the new week's scores.
// Past winners are recorded here for the "Winners" tab.
//
// AUDIT NOTES (updated 2026-03-10):
//   Week boundary:  Monday 00:00:00 UTC → Sunday 23:59:59 UTC
//   Tie-break:      Highest score wins. If tied, earliest timestamp wins.
//   Exclusions:     None — all submitted runs are eligible.
//   Data source:    Google Sheets via Apps Script (run logs exported to
//                   data/runs.csv for offline winner computation).
//   Computation:    scripts/compute-winners.js
//   All weeks computed from full dataset (213 runs, Dec 26 2025 – Mar 25 2026).
//   Current week (Mar 24 – Mar 30, 2026) is in progress.
//
// TO ADD A NEW WINNER: Add an entry to WEEKLY_WINNERS with:
//   weekNumber, weekStart (Monday), weekEnd (Sunday), playerName, score,
//   characterName, characterId, side
// ═══════════════════════════════════════════════════════════════════════════

const WEEKLY_WINNERS = [
  // Week 1 — Jan 19–25, 2026 — Winner: Tom (47,810)
  {
    weekNumber: 1,
    weekStart: '2026-01-19',
    weekEnd: '2026-01-25',
    playerName: 'Tom',
    score: 47810,
    characterName: 'Cloak #16564',
    characterId: 'c_16564',
    side: 'cloak',
  },
  // Week 2 — Jan 26 – Feb 1, 2026 — Winner: . (46,710)
  {
    weekNumber: 2,
    weekStart: '2026-01-26',
    weekEnd: '2026-02-01',
    playerName: '.',
    score: 46710,
    characterName: 'Cloak #16564',
    characterId: 'c_16564',
    side: 'cloak',
  },
  // Week 3 — Feb 2–8, 2026 — Winner: Rudy (46,510)
  {
    weekNumber: 3,
    weekStart: '2026-02-02',
    weekEnd: '2026-02-08',
    playerName: 'Rudy',
    score: 46510,
    characterName: 'Cloak #16564',
    characterId: 'c_16564',
    side: 'cloak',
  },
  // Week 4 — Feb 9–15, 2026 — Winner: Eichehuehnchen (45,470)
  {
    weekNumber: 4,
    weekStart: '2026-02-09',
    weekEnd: '2026-02-15',
    playerName: 'Eichehuehnchen',
    score: 45470,
    characterName: 'Migo #19130',
    characterId: 'N_19130',
    side: 'migo',
  },
  // Week 5 — Feb 16–22, 2026 — Winner: Shivansh (47,000)
  {
    weekNumber: 5,
    weekStart: '2026-02-16',
    weekEnd: '2026-02-22',
    playerName: 'Shivansh',
    score: 47000,
    characterName: 'Cloak #151',
    characterId: 'c_151',
    side: 'cloak',
  },
  // Week 6 — Feb 23 – Mar 1, 2026 — Winner: Shivansh (46,140)
  {
    weekNumber: 6,
    weekStart: '2026-02-23',
    weekEnd: '2026-03-01',
    playerName: 'Shivansh',
    score: 46140,
    characterName: 'Cloak #151',
    characterId: 'c_151',
    side: 'cloak',
  },
  // Week 7 — Mar 2–8, 2026 — Winner: Re (39,130)
  {
    weekNumber: 7,
    weekStart: '2026-03-02',
    weekEnd: '2026-03-08',
    playerName: 'Re',
    score: 39130,
    characterName: 'Cloak #19431',
    characterId: 'c_19431',
    side: 'cloak',
  },
  // Week 8 — Mar 9–15, 2026 — Winner: Bobby (45,580)
  {
    weekNumber: 8,
    weekStart: '2026-03-09',
    weekEnd: '2026-03-15',
    playerName: 'Bobby',
    score: 45580,
    characterName: 'Cloak #151',
    characterId: 'c_151',
    side: 'cloak',
  },
  // Week 9 — Mar 16–22, 2026 — Winner: walmy (46,560)
  {
    weekNumber: 9,
    weekStart: '2026-03-16',
    weekEnd: '2026-03-22',
    playerName: 'walmy',
    score: 46560,
    characterName: 'Cloak #151',
    characterId: 'c_151',
    side: 'cloak',
  },
  // Week 10 — Mar 23–29, 2026 — Winner: Ben (43,400)
  {
    weekNumber: 10,
    weekStart: '2026-03-23',
    weekEnd: '2026-03-29',
    playerName: 'Ben',
    score: 43400,
    characterName: 'Cloak #151',
    characterId: 'c_151',
    side: 'cloak',
  },
];

/**
 * Get the Monday 00:00 UTC of the week containing the given date.
 */
function getWeekStartMonday(date = new Date()) {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? 6 : day - 1; // days since Monday
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
}

/**
 * Get the Sunday 23:59:59 UTC of the week containing the given date.
 */
function getWeekEndSunday(date = new Date()) {
  const monday = getWeekStartMonday(date);
  const sunday = new Date(monday);
  sunday.setUTCDate(sunday.getUTCDate() + 6);
  sunday.setUTCHours(23, 59, 59, 999);
  return sunday;
}

/**
 * Format a date as "Mon DD" (e.g. "Jan 19")
 */
function formatShortDate(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

// Helper function to get leaderboard image (tries URL mapping first, then character ID)
function getLeaderboardImage(originalUrl, characterId) {
  // Try URL mapping first
  if (LEADERBOARD_IMAGE_MAP[originalUrl]) {
    return LEADERBOARD_IMAGE_MAP[originalUrl];
  }
  // Try character ID mapping as fallback (case-insensitive)
  if (characterId) {
    const normalizedId = characterId.toLowerCase();
    if (CHARACTER_ID_IMAGE_MAP[normalizedId]) {
      return CHARACTER_ID_IMAGE_MAP[normalizedId];
    }
  }
  // Return original URL if no mapping found
  return originalUrl;
}

class BeanWarsApp {
  constructor() {
    this.currentScreen = 'home';
    this.selectedSide = null;
    this.selectedCharacter = null;
    this.playerName = '';
    this.playerEmail = '';
    this.playerWallet = '';
    this.gameEngine = null;
    this.inputManager = null;
    this.animationFrame = null;
    this.canvas = document.getElementById('game-canvas');
    this.beansCollected = 0;
    this.tutorialShown = false; // Track if special tutorial has been shown
    this.tutorialTimer = null; // Timer for auto-hiding tutorial
    this.scoreSubmitted = false; // Prevent duplicate score submissions
    this.cachedLeaderboard = null; // Preloaded leaderboard data
    this.lastSubmittedScore = null; // Track last submitted score for highlighting

    this.initialize();
  }

  initialize() {
    audioManager.init();
    this.setupEventListeners();
    this.setupMarquees();
    this.loadTheme(); // Load saved theme
    this.preloadLeaderboard(); // Preload leaderboard data in background
    this.showScreen('home');
  }

  // Preload all leaderboard data in background for faster display
  async preloadLeaderboard() {
    try {
      const scores = await leaderboardManager.getScores('all');
      this.cachedLeaderboard = scores;
      this._leaderboardCacheTime = Date.now();
      console.log('[Leaderboard] Preloaded', scores?.length || 0, 'scores');
      // Log first score's timestamp format for debugging
      if (scores?.length > 0) {
        const s = scores[0];
        console.log('[Leaderboard] Sample timestamp fields:', {
          created_at: s.created_at, timestamp: s.timestamp, Timestamp: s.Timestamp
        });
      }
    } catch (error) {
      console.error('[Leaderboard] Preload failed:', error);
    }
  }

  setupEventListeners() {
    // Helper function to add both click and touch events for mobile compatibility
    const addClickAndTouch = (elementId, handler) => {
      const element = document.getElementById(elementId);
      if (!element) return;

      element.addEventListener('click', handler);
      element.addEventListener('touchend', (e) => {
        e.preventDefault(); // Prevent double-firing on mobile
        handler(e);
      });
    };

    // Home screen - name validation
    const nameInput = document.getElementById('home-player-name-input');
    const cloaksBtn = document.getElementById('btn-home-cloaks');
    const migosBtn = document.getElementById('btn-home-migos');
    const nameError = document.getElementById('name-error');

    if (nameInput) {
      nameInput.addEventListener('input', (e) => {
        const name = e.target.value.trim();
        const isValid = name.length > 0;

        // Enable/disable buttons based on name validity
        if (cloaksBtn) cloaksBtn.disabled = !isValid;
        if (migosBtn) migosBtn.disabled = !isValid;

        // Hide error message when typing
        if (nameError) nameError.style.display = 'none';
      });
    }

    addClickAndTouch('btn-home-cloaks', () => {
      if (this.validateName()) {
        this.selectSide('cloak');
      }
    });

    addClickAndTouch('btn-home-migos', () => {
      if (this.validateName()) {
        this.selectSide('migo');
      }
    });

    addClickAndTouch('btn-view-leaderboard', () => {
      this.showLeaderboard();
    });

    addClickAndTouch('btn-mute-home', () => {
      this.toggleMute('btn-mute-home');
    });

    // Character screen
    addClickAndTouch('btn-characters-back-top', () => {
      this.showScreen('home');
    });

    addClickAndTouch('btn-characters-continue', () => {
      this.startGame();
    });

    addClickAndTouch('btn-howto-open', () => {
      document.getElementById('howto-overlay')?.classList.add('active');
    });

    addClickAndTouch('btn-howto-close', () => {
      document.getElementById('howto-overlay')?.classList.remove('active');
    });

    // Game screen
    addClickAndTouch('btn-menu', () => {
      this.showPauseMenu();
    });

    addClickAndTouch('btn-mute-game', () => {
      this.toggleMute('btn-mute-game');
    });

    addClickAndTouch('btn-special', () => {
      if (this.gameEngine && this.gameEngine.player) {
        this.gameEngine.player.activateSpecial();
        this.hideSpecialTutorial(); // Hide tutorial on first use
      }
    });

    // Pause menu
    addClickAndTouch('btn-resume', () => {
      this.resumeGame();
    });

    addClickAndTouch('btn-menu-key', () => {
      this.showKeyPanel();
    });

    addClickAndTouch('btn-key-back', () => {
      this.showMenuPanel();
    });

    addClickAndTouch('btn-menu-mute', () => {
      this.toggleMute();
      this.updateMuteButtons();
    });

    addClickAndTouch('btn-exit-to-menu', () => {
      this.exitToMenu();
    });

    // Theme buttons
    document.querySelectorAll('.theme-btn').forEach(btn => {
      const handler = (e) => {
        const theme = e.target.dataset.theme;
        this.setTheme(theme);
      };
      btn.addEventListener('click', handler);
      btn.addEventListener('touchend', (e) => {
        e.preventDefault();
        handler(e);
      });
    });

    // Game over
    addClickAndTouch('btn-gameover-record', () => {
      this.recordScore('gameover');
    });

    addClickAndTouch('btn-gameover-skip', () => {
      this.showScreen('home');
    });

    addClickAndTouch('btn-gameover-visit-cafe', () => {
      window.open(BLACKBIRD_CAFE_URL, '_blank');
    });

    addClickAndTouch('btn-gameover-leaderboard', () => {
      this.showLeaderboard();
    });

    // Victory
    addClickAndTouch('btn-victory-skip', () => {
      this.showScreen('home');
    });

    addClickAndTouch('btn-victory-leaderboard', () => {
      this.showLeaderboard();
    });

    addClickAndTouch('btn-victory-visit-cafe', () => {
      window.open(BLACKBIRD_CAFE_URL, '_blank');
    });

    // Leaderboard
    addClickAndTouch('btn-leaderboard-play', () => {
      this.showScreen('home');
    });

    addClickAndTouch('btn-leaderboard-menu', () => {
      this.showScreen('home');
    });

    // Leaderboard tabs
    document.querySelectorAll('.lb-tab').forEach(tab => {
      const handler = (e) => {
        const filter = e.target.dataset.filter;
        this.filterLeaderboard(filter);
      };
      tab.addEventListener('click', handler);
      tab.addEventListener('touchend', (e) => {
        e.preventDefault();
        handler(e);
      });
    });

    // Player info inputs (home screen)
    document.getElementById('home-player-name-input')?.addEventListener('input', (e) => {
      this.playerName = e.target.value;
    });

    // Email modal handlers
    addClickAndTouch('btn-email-modal-submit', () => {
      this.submitScoreWithEmail();
    });

    addClickAndTouch('btn-email-modal-skip', () => {
      this.submitScoreWithoutEmail();
    });
  }

  setupMarquees() {
    // Setup marquees - duplicate content for seamless CSS animation loop
    // CSS handles the actual animation (smoother than JS setInterval)
    const setupMarquee = (trackId) => {
      const track = document.getElementById(trackId);
      if (!track) return;

      // Pre-load all images to prevent mobile loading issues
      const images = Array.from(track.children);
      images.forEach(img => {
        // Force eager loading on clones
        img.loading = 'eager';
        img.decoding = 'sync';
        // Pre-load by creating a new Image object
        const preload = new Image();
        preload.src = img.src;
      });

      // Duplicate images for seamless infinite scroll
      // CSS animation translates 0% to -50%, then loops seamlessly
      const clone = images.map(img => {
        const clonedImg = img.cloneNode(true);
        clonedImg.loading = 'eager';
        clonedImg.decoding = 'sync';
        return clonedImg;
      });
      clone.forEach(img => track.appendChild(img));
    };

    setupMarquee('marquee-cloaks');
    setupMarquee('marquee-migos');
  }

  validateName() {
    const nameInput = document.getElementById('home-player-name-input');
    const nameError = document.getElementById('name-error');
    const name = nameInput?.value.trim() || '';

    if (name.length === 0) {
      if (nameError) nameError.style.display = 'block';
      return false;
    }

    return true;
  }

  selectSide(side) {
    this.selectedSide = side;

    // Read name, email, and wallet from home screen when transitioning to character select
    const homeNameInput = document.getElementById('home-player-name-input');
    const homeEmailInput = document.getElementById('home-player-email-input');
    const homeWalletInput = document.getElementById('home-player-wallet-input');
    if (homeNameInput) this.playerName = homeNameInput.value;
    if (homeEmailInput) this.playerEmail = homeEmailInput.value.trim();
    if (homeWalletInput) this.playerWallet = homeWalletInput.value.trim();

    this.showScreen('characters');
    this.renderCharacterSelect();
  }

  renderCharacterSelect() {
    const list = document.getElementById('character-list');
    const heading = document.getElementById('character-select-heading');
    const screen = document.getElementById('screen-characters');
    const howToSection = document.getElementById('character-how-to-play');
    const continueBtn = document.getElementById('btn-characters-continue');

    if (!list || !screen) return;

    // Reset selection state
    this.selectedCharacter = null;
    list.innerHTML = '';

    // Set heading based on side: "Select your Cloak" or "Select your Migo"
    if (heading) {
      heading.textContent = this.selectedSide === 'cloak' ? 'Select your Cloak' : 'Select your Migo';
    }

    // Set mode class for styling
    screen.className = 'screen active';
    if (this.selectedSide === 'migo') {
      screen.classList.add('mode-migo');
    }

    // Show How to Play section initially
    if (howToSection) howToSection.style.display = 'block';

    // Clear detail card container when showing characters
    const detailContainer = document.getElementById('character-detail-container');
    if (detailContainer) {
      detailContainer.innerHTML = '';
    }

    // Disable Start Game button initially
    if (continueBtn) {
      continueBtn.disabled = true;
      continueBtn.textContent = 'Choose your character';
    }

    const characters = this.selectedSide === 'cloak' ? CHARACTERS.cloaks : CHARACTERS.migos;

    characters.forEach(char => {
      const card = document.createElement('div');
      card.className = 'char-card';
      card.innerHTML = `
        <div class="char-portrait">
          <img src="${char.sprite}" alt="${char.name}">
        </div>
        <div class="char-name">${char.displayName || char.name}</div>
        <div class="char-side-label">${char.side.toUpperCase()}</div>
      `;

      // Add both click and touch handlers for reliable mobile interaction
      const selectHandler = (e) => {
        e.preventDefault(); // Prevent double-firing on mobile
        this.selectCharacter(char, card);
      };

      card.addEventListener('click', selectHandler);
      card.addEventListener('touchend', selectHandler);

      // Make entire card area tappable with proper touch feedback
      card.style.cursor = 'pointer';
      card.style.touchAction = 'manipulation';
      card.style.webkitTapHighlightColor = 'transparent';
      card.style.userSelect = 'none';
      card.style.webkitUserSelect = 'none';
      // Grid layout with CSS handles sizing for compact, consistent cards

      list.appendChild(card);
    });
  }

  selectCharacter(character, cardElement) {
    this.selectedCharacter = character;

    // Update UI
    document.querySelectorAll('.char-card').forEach(c => c.classList.remove('selected'));
    cardElement.classList.add('selected');

    // Hide how-to section and show stats
    const howToSection = document.getElementById('character-how-to-play');
    if (howToSection) howToSection.style.display = 'none';

    // Enable and update start button
    const continueBtn = document.getElementById('btn-characters-continue');
    if (continueBtn) {
      continueBtn.disabled = false;
      continueBtn.textContent = 'Start Game';
    }

    // Clear previous detail card from container
    const detailContainer = document.getElementById('character-detail-container');
    if (detailContainer) {
      detailContainer.innerHTML = '';
    }

    // Helper function to create stat meter
    const createStatMeter = (label, value, maxValue, color) => {
      const percentage = Math.min((value / maxValue) * 100, 100);
      return `
        <div class="stat-row">
          <div class="stat-label">${label}</div>
          <div class="stat-meter">
            <div class="stat-meter-fill" style="width: ${percentage}%; background-color: ${color};"></div>
          </div>
          <div class="stat-value">${Math.round(value)}</div>
        </div>
      `;
    };

    // Add compact detail card with stats
    const detailCard = document.createElement('div');
    detailCard.className = 'char-detail-card';

    // Calculate stat percentages (max values based on character ranges)
    const speedPercent = Math.min((character.speed / 250) * 100, 100);
    const fireRatePercent = Math.min((character.fireRate / 5) * 100, 100);
    const bulletSpeedPercent = Math.min((character.bulletSpeed / 360) * 100, 100);

    detailCard.innerHTML = `
      <div class="char-detail-header">
        <div class="char-detail-portrait">
          <img src="${character.sprite}" alt="${character.name}">
        </div>
        <div class="char-detail-info">
          <div class="char-detail-name">${character.displayName || character.name}</div>
          <div class="char-detail-projectile">
            <span class="projectile-label">Projectile:</span>
            <img src="${character.projectile}" alt="Projectile" class="projectile-preview">
          </div>
        </div>
      </div>

      <div class="char-stats" style="margin-top:6px;padding:5px 8px;background:rgba(127,208,255,0.08);border-radius:8px;border:1px solid rgba(127,208,255,0.2);">
        <div class="stat-row" style="display:flex;align-items:center;gap:6px;margin-bottom:3px;">
          <span style="font-size:0.62rem;color:var(--muted);width:55px;">Speed</span>
          <div style="flex:1;height:6px;background:rgba(255,255,255,0.1);border-radius:3px;overflow:hidden;">
            <div style="width:${speedPercent}%;height:100%;background:linear-gradient(90deg,#7fd0ff,#63b5ff);border-radius:3px;"></div>
          </div>
        </div>
        <div class="stat-row" style="display:flex;align-items:center;gap:6px;margin-bottom:3px;">
          <span style="font-size:0.62rem;color:var(--muted);width:55px;">Fire Rate</span>
          <div style="flex:1;height:6px;background:rgba(255,255,255,0.1);border-radius:3px;overflow:hidden;">
            <div style="width:${fireRatePercent}%;height:100%;background:linear-gradient(90deg,#ffe38f,#ffb673);border-radius:3px;"></div>
          </div>
        </div>
        <div class="stat-row" style="display:flex;align-items:center;gap:6px;">
          <span style="font-size:0.62rem;color:var(--muted);width:55px;">Bullet Spd</span>
          <div style="flex:1;height:6px;background:rgba(255,255,255,0.1);border-radius:3px;overflow:hidden;">
            <div style="width:${bulletSpeedPercent}%;height:100%;background:linear-gradient(90deg,#ff6b9d,#ff4b6a);border-radius:3px;"></div>
          </div>
        </div>
      </div>

      <div class="char-detail-ability" style="margin-top:5px;">
        <span class="ability-title" style="font-weight:700;">Special:</span>
        <span class="ability-desc" style="margin-left:4px;">${character.specialDescription}</span>
      </div>
    `;

    // Append to dedicated detail container, not the character grid
    if (detailContainer) {
      detailContainer.appendChild(detailCard);
    }
  }

  startGame() {
    if (!this.selectedCharacter) {
      alert('Please select a character first!');
      return;
    }

    // Get player name, email, and wallet from home screen (already captured in selectSide, but re-read to be safe)
    const homeNameInput = document.getElementById('home-player-name-input');
    const homeEmailInput = document.getElementById('home-player-email-input');
    const homeWalletInput = document.getElementById('home-player-wallet-input');
    if (homeNameInput) this.playerName = homeNameInput.value;
    if (homeEmailInput && homeEmailInput.value.trim()) {
      this.playerEmail = homeEmailInput.value.trim();
    }
    if (homeWalletInput && homeWalletInput.value.trim()) {
      this.playerWallet = homeWalletInput.value.trim();
    }

    // Reset beans collected and score submission flag
    this.beansCollected = 0;
    this.scoreSubmitted = false;

    this.showScreen('game');
    this.initializeGame();
  }

  initializeGame() {
    // Create input manager
    this.inputManager = new InputManager();
    const joystickBase = document.getElementById('joystick-base');
    const joystickThumb = document.getElementById('joystick-thumb');
    this.inputManager.setupJoystick(joystickBase, joystickThumb);

    // Update player info HUD
    const playerPortrait = document.getElementById('hud-player-portrait');
    const playerName = document.getElementById('hud-player-name');
    const characterName = document.getElementById('hud-character-name');

    if (playerPortrait) playerPortrait.src = this.selectedCharacter.sprite;
    if (playerName) playerName.textContent = this.playerName || 'Pilot';
    if (characterName) characterName.textContent = this.selectedCharacter.displayName || this.selectedCharacter.name;

    // Update top-right character portrait and name
    const topCharacterPortrait = document.getElementById('hud-top-character-portrait');
    if (topCharacterPortrait) topCharacterPortrait.src = this.selectedCharacter.sprite;
    const topCharacterName = document.getElementById('hud-character-name');
    if (topCharacterName) topCharacterName.textContent = this.selectedCharacter.displayName || this.selectedCharacter.name;

    // Create game engine
    this.gameEngine = new GameEngine(this.canvas, this.selectedCharacter);

    // Start game loop
    this.startGameLoop();
  }

  startGameLoop() {
    const loop = () => {
      if (!this.gameEngine) return;

      const input = this.inputManager.getInput();

      // Hide tutorial if special key was pressed
      if (input.special) {
        this.hideSpecialTutorial();
      }

      this.gameEngine.update(input);
      this.gameEngine.render();

      // Update bean counters and special meters
      this.updateGameHUD();

      // Check for game over or victory
      if (this.gameEngine.isGameOver) {
        this.stopGameLoop();
        if (this.gameEngine.state === 'victory') {
          this.showVictory();
        } else {
          this.showGameOver();
        }
        return;
      }

      this.animationFrame = requestAnimationFrame(loop);
    };

    this.animationFrame = requestAnimationFrame(loop);
  }

  updateGameHUD() {
    if (!this.gameEngine || !this.gameEngine.player) return;

    const player = this.gameEngine.player;

    // Update horizontal special meter at top
    const specialCharge = player.specialCharge;
    const fillHorizontal = document.getElementById('special-meter-horizontal-fill');
    if (fillHorizontal) fillHorizontal.style.width = `${specialCharge}%`;

    // Update banked specials count
    const bankedEl = document.getElementById('hud-specials-banked');
    if (bankedEl) bankedEl.textContent = player.specialsBanked;

    // Update special status text
    const specialStatus = document.getElementById('hud-special-status');
    if (specialStatus) {
      if (player.specialActive) {
        specialStatus.textContent = 'ACTIVE!';
        specialStatus.style.color = '#7fd0ff';
      } else if (player.specialsBanked > 0) {
        specialStatus.textContent = 'Ready';
        specialStatus.style.color = '#ffe38f';
      } else {
        specialStatus.textContent = 'Charging';
        specialStatus.style.color = '#8fa0d6';
      }
    }

    // Update bottom hearts display
    const heartsBottom = document.getElementById('hud-hearts-bottom');
    if (heartsBottom) heartsBottom.textContent = player.lives;

    // Add pulse animation when at 1 heart
    const heartsDisplay = heartsBottom?.closest('.hearts-display');
    if (heartsDisplay) {
      if (player.lives === 1) {
        heartsDisplay.classList.add('low-health');
      } else {
        heartsDisplay.classList.remove('low-health');
      }
    }

    // Update special button state
    const specialBtn = document.getElementById('btn-special');
    if (specialBtn) {
      specialBtn.disabled = player.specialsBanked === 0 || player.specialActive;
    }

    // Update special logo button (new HUD)
    const specialLogoBtn = document.getElementById('btn-special');
    if (specialLogoBtn) {
      specialLogoBtn.disabled = player.specialsBanked === 0 || player.specialActive;
    }

    // Update special charge dots (old UI elements)
    for (let i = 1; i <= 3; i++) {
      const dot = document.getElementById(`charge-dot-${i}`);
      if (dot) {
        if (i <= player.specialsBanked) {
          dot.classList.add('filled');
        } else {
          dot.classList.remove('filled');
        }
      }
    }

    // Update special lights (new HUD)
    for (let i = 1; i <= 3; i++) {
      const light = document.getElementById(`light-${i}`);
      if (light) {
        if (i <= player.specialsBanked) {
          light.classList.add('active');
        } else {
          light.classList.remove('active');
        }
      }
    }

    // Tutorial tooltip disabled - users find it intrusive during gameplay
    // if (!this.tutorialShown && player.specialsBanked > 0) {
    //   this.showSpecialTutorial();
    // }
  }

  showSpecialTutorial() {
    const tutorial = document.getElementById('special-tutorial');
    if (tutorial) {
      tutorial.classList.remove('hidden');
      this.tutorialShown = true;

      // Auto-hide after 5 seconds
      this.tutorialTimer = setTimeout(() => {
        tutorial.classList.add('hidden');
      }, 5000);
    }
  }

  hideSpecialTutorial() {
    const tutorial = document.getElementById('special-tutorial');
    if (tutorial) {
      tutorial.classList.add('hidden');
      if (this.tutorialTimer) {
        clearTimeout(this.tutorialTimer);
        this.tutorialTimer = null;
      }
    }
  }

  stopGameLoop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  showPauseMenu() {
    if (this.gameEngine) {
      this.gameEngine.pause();
    }
    document.getElementById('pause-overlay')?.classList.add('active');
    this.showMenuPanel();
  }

  resumeGame() {
    if (this.gameEngine) {
      this.gameEngine.resume();
    }
    document.getElementById('pause-overlay')?.classList.remove('active');
  }

  showMenuPanel() {
    document.getElementById('pause-menu-panel')?.classList.remove('hidden');
    document.getElementById('pause-key-panel')?.classList.add('hidden');
  }

  showKeyPanel() {
    document.getElementById('pause-menu-panel')?.classList.add('hidden');
    document.getElementById('pause-key-panel')?.classList.remove('hidden');
  }

  exitToMenu() {
    this.stopGameLoop();
    this.gameEngine = null;
    document.getElementById('pause-overlay')?.classList.remove('active');
    this.showScreen('home');
  }

  showGameOver() {
    // Play game over sound
    audioManager.gameOver();

    // Safely get stats with fallback
    const stats = this.gameEngine ? this.gameEngine.getGameStats() : {
      score: 0,
      level: 1,
      runTime: 0,
      specialsUsed: 0,
      heartsRemaining: 0
    };

    // Safely get character data with fallback
    const character = this.selectedCharacter || {
      sprite: '',
      displayName: 'Unknown',
      name: 'Unknown',
      side: 'cloak'
    };

    const charImg = document.getElementById('gameover-character-img');
    if (charImg && character.sprite) {
      charImg.src = character.sprite;
    }

    // Set avatar background color based on side (tint the div, not the border)
    const portraitDiv = charImg?.parentElement;
    if (portraitDiv) {
      const sideColor = character.side === 'cloak' ? '#5b6170' : '#79a4f9';
      portraitDiv.style.backgroundColor = sideColor;
    }

    // Set player name from home screen
    const playerNameEl = document.getElementById('gameover-player-name');
    if (playerNameEl) playerNameEl.textContent = this.playerName || 'Player';

    // Set character name with "Playing as:" prefix
    const charName = document.getElementById('gameover-character-name');
    if (charName) charName.textContent = `Playing as: ${character.displayName || character.name}`;

    // Set side label (for "Cloaks: 1234" or "Migos: 1234" format)
    const sideLabel = document.getElementById('gameover-side-label');
    if (sideLabel) sideLabel.textContent = character.side === 'cloak' ? 'Cloaks' : 'Migos';

    // Set score
    const scoreEl = document.getElementById('gameover-score');
    if (scoreEl) scoreEl.textContent = stats.score;

    const timeEl = document.getElementById('gameover-time');
    if (timeEl) timeEl.textContent = leaderboardManager.formatTime(stats.runTime);

    const specialsEl = document.getElementById('gameover-specials');
    if (specialsEl) specialsEl.textContent = stats.specialsUsed;

    const heartsEl = document.getElementById('gameover-hearts');
    if (heartsEl) heartsEl.textContent = stats.heartsRemaining;

    this.showScreen('gameover');

    // Auto-submit score if email was provided
    this.autoSubmitScore();
  }

  showVictory() {
    // Safely get stats with fallback
    const stats = this.gameEngine ? this.gameEngine.getGameStats() : {
      score: 0,
      level: 1,
      runTime: 0,
      specialsUsed: 0,
      heartsRemaining: 0
    };

    // Safely get character data with fallback
    const character = this.selectedCharacter || {
      sprite: '',
      displayName: 'Unknown',
      name: 'Unknown',
      side: 'cloak'
    };

    const charImg = document.getElementById('victory-character-img');
    if (charImg && character.sprite) {
      charImg.src = character.sprite;
    }

    // Set avatar background color based on side (tint the div, not the border)
    const portraitDiv = charImg?.parentElement;
    if (portraitDiv) {
      const sideColor = character.side === 'cloak' ? '#5b6170' : '#79a4f9';
      portraitDiv.style.backgroundColor = sideColor;
    }

    // Set player name from home screen
    const playerNameEl = document.getElementById('victory-player-name');
    if (playerNameEl) playerNameEl.textContent = this.playerName || 'Player';

    // Set character name with "Playing as:" prefix
    const charName = document.getElementById('victory-character-name');
    if (charName) charName.textContent = `Playing as: ${character.displayName || character.name}`;

    // Set side label (for "Cloaks: 1234" or "Migos: 1234" format)
    const sideLabel = document.getElementById('victory-side-label');
    if (sideLabel) sideLabel.textContent = character.side === 'cloak' ? 'Cloaks' : 'Migos';

    // Set score
    const scoreEl = document.getElementById('victory-score');
    if (scoreEl) scoreEl.textContent = stats.score;

    const timeEl = document.getElementById('victory-time');
    if (timeEl) timeEl.textContent = leaderboardManager.formatTime(stats.runTime);

    const specialsEl = document.getElementById('victory-specials');
    if (specialsEl) specialsEl.textContent = stats.specialsUsed;

    const heartsEl = document.getElementById('victory-hearts');
    if (heartsEl) heartsEl.textContent = stats.heartsRemaining;

    this.showScreen('victory');
    this.startConfetti();

    // Auto-submit score if email was provided
    this.autoSubmitScore();
  }

  startConfetti() {
    const canvas = document.getElementById('confetti-canvas');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    const confetti = [];
    const colors = ['#7fd0ff', '#ffe38f', '#ff6b9d', '#8fa0d6'];

    for (let i = 0; i < 50; i++) {
      confetti.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: Math.random() * 2 + 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 8 + 4
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confetti.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.y > canvas.height) {
          p.y = -10;
          p.x = Math.random() * canvas.width;
        }

        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
      });

      requestAnimationFrame(animate);
    };

    animate();
  }

  async recordScore(screen) {
    // Check if player name exists (should be from home screen)
    if (!this.playerName || !this.playerName.trim()) {
      alert('Please enter your name on the main menu before playing!');
      return;
    }

    // Store which screen we're coming from
    this.recordScoreScreen = screen;

    // Use email/wallet from home screen (already stored in this.playerEmail/playerWallet)
    const email = this.playerEmail && this.isValidEmail(this.playerEmail) ? this.playerEmail : '';
    const wallet = this.playerWallet && this.isValidWallet(this.playerWallet) ? this.playerWallet : '';

    // Either email or wallet is required for prize eligibility, but we'll submit anyway
    // Submit score with email and wallet
    await this.submitScore(email, wallet);
  }

  async submitScoreWithEmail() {
    const emailInput = document.getElementById('email-modal-input');
    const walletInput = document.getElementById('wallet-modal-input');
    const errorDiv = document.getElementById('email-modal-error');
    const email = emailInput?.value.trim() || '';
    const wallet = walletInput?.value.trim() || '';

    // Validate email if provided
    if (email && !this.isValidEmail(email)) {
      if (errorDiv) {
        errorDiv.textContent = 'Please enter a valid email address or leave blank to skip.';
        errorDiv.style.display = 'block';
      }
      return;
    }

    // Validate wallet if provided
    if (wallet && !this.isValidWallet(wallet)) {
      if (errorDiv) {
        errorDiv.textContent = 'Please enter a valid Ethereum wallet address (0x...) or leave blank to skip.';
        errorDiv.style.display = 'block';
      }
      return;
    }

    // Store wallet for this submission
    if (wallet) this.playerWallet = wallet;
    if (email) this.playerEmail = email;

    // Submit score
    await this.submitScore(email, wallet);
  }

  async submitScoreWithoutEmail() {
    // Submit score without modal email/wallet, but still use home screen values if available
    const email = this.playerEmail && this.isValidEmail(this.playerEmail) ? this.playerEmail : '';
    const wallet = this.playerWallet && this.isValidWallet(this.playerWallet) ? this.playerWallet : '';
    await this.submitScore(email, wallet);
  }

  async autoSubmitScore() {
    // Only auto-submit if not already submitted
    if (this.scoreSubmitted) {
      console.log('[Leaderboard] Score already submitted, skipping auto-submit');
      return;
    }

    // Check if player has a name (required for leaderboard)
    if (!this.playerName || !this.playerName.trim()) {
      console.log('[Leaderboard] No player name provided, skipping auto-submit');
      const gameoverStatus = document.getElementById('gameover-score-status');
      const victoryStatus = document.getElementById('victory-score-status');
      const noNameMessage = '<div style="font-size:0.72rem;color:var(--muted);font-weight:600;margin-bottom:2px;">Score not recorded</div><div style="font-size:0.64rem;color:var(--muted);line-height:1.4;">Enter your name on the main menu to join the leaderboard.</div>';
      if (gameoverStatus) gameoverStatus.innerHTML = noNameMessage;
      if (victoryStatus) victoryStatus.innerHTML = noNameMessage;
      return;
    }

    // Auto-submit the score (with or without email/wallet)
    // Email/wallet is optional - scores without either appear on "Overall" but not "Prizes"
    const email = this.playerEmail && this.isValidEmail(this.playerEmail) ? this.playerEmail : '';
    const wallet = this.playerWallet && this.isValidWallet(this.playerWallet) ? this.playerWallet : '';
    await this.submitScore(email, wallet, true);
  }

  async submitScore(email, wallet = '', isAutoSubmit = false) {
    // Prevent duplicate submissions
    if (this.scoreSubmitted) {
      console.log('[Leaderboard] Score already submitted, preventing duplicate');
      return;
    }

    const stats = this.gameEngine.getGameStats();
    const scoreData = {
      playerName: this.playerName.trim(),
      playerEmail: email,
      playerWallet: wallet,
      characterId: this.selectedCharacter.id,
      characterName: this.selectedCharacter.name,
      characterImage: this.selectedCharacter.sprite, // Store character sprite URL
      side: this.selectedCharacter.side,
      score: stats.score,
      levelReached: stats.level,
      runTime: stats.runTime,
      specialsUsed: stats.specialsUsed,
      heartsRemaining: stats.heartsRemaining
    };

    // Mark as submitted immediately to prevent race conditions
    this.scoreSubmitted = true;

    try {
      const result = await leaderboardManager.saveScore(scoreData);

      // Check if submission was successful
      if (!result.success) {
        console.error('Score submission failed:', result.error);
        // Only show alert for manual submissions
        if (!isAutoSubmit) {
          const errorMsg = result.error?.message || 'Failed to save score. Please try again.';
          alert(`Could not save score: ${errorMsg}`);
        }
        // Update status message
        const gameoverStatus = document.getElementById('gameover-score-status');
        const victoryStatus = document.getElementById('victory-score-status');
        const errorMessage = '<div style="font-size:0.72rem;color:#ff6b9d;font-weight:600;margin-bottom:2px;">Could not save score</div><div style="font-size:0.64rem;color:var(--muted);line-height:1.4;">Score saved locally. Try again later.</div>';
        if (gameoverStatus) gameoverStatus.innerHTML = errorMessage;
        if (victoryStatus) victoryStatus.innerHTML = errorMessage;
      } else {
        console.log('Score submitted successfully!');

        // Store score info for highlighting on leaderboard
        this.lastSubmittedScore = {
          playerName: scoreData.playerName,
          score: scoreData.score,
          characterId: scoreData.characterId
        };

        // Calculate placement by fetching fresh leaderboard
        const freshScores = await leaderboardManager.getScores('all');
        this.cachedLeaderboard = freshScores; // Update cache
        let placement = freshScores.findIndex(s =>
          s.player_name === scoreData.playerName && s.score === scoreData.score
        ) + 1;

        // If not found in top scores, estimate placement
        if (placement === 0) {
          placement = freshScores.filter(s => s.score > scoreData.score).length + 1;
        }

        // Update status message with placement
        const gameoverStatus = document.getElementById('gameover-score-status');
        const victoryStatus = document.getElementById('victory-score-status');
        const placementText = placement <= 10
          ? `You placed <strong style="color:var(--gold);">#${placement}</strong> on the leaderboard!`
          : `You placed #${placement}. Top 10 shown on leaderboard.`;
        const hasEmail = email && this.isValidEmail(email);
        const hasWallet = wallet && this.isValidWallet(wallet);
        const prizeEligible = hasEmail || hasWallet;
        const prizeText = prizeEligible
          ? '<div style="font-size:0.62rem;color:var(--gold);margin-top:2px;">Eligible for prizes!</div>'
          : '<div style="font-size:0.62rem;color:var(--muted);margin-top:2px;">Add email or wallet on main menu for prize eligibility.</div>';
        const successMessage = `<div style="font-size:0.72rem;color:var(--accent);font-weight:600;margin-bottom:2px;">Score recorded!</div><div style="font-size:0.68rem;color:var(--ink);line-height:1.4;">${placementText}</div>${prizeText}`;
        if (gameoverStatus) gameoverStatus.innerHTML = successMessage;
        if (victoryStatus) victoryStatus.innerHTML = successMessage;
      }

      // Hide modal if visible
      const modal = document.getElementById('email-modal-overlay');
      if (modal) modal.classList.remove('active');

      // Only navigate to leaderboard for manual submissions
      if (!isAutoSubmit) {
        this.showLeaderboard();
      }
    } catch (error) {
      console.error('Error submitting score:', error);
      if (!isAutoSubmit) {
        alert('Error saving score. Please try again.');
      }
    }
  }

  isValidEmail(email) {
    // Simple email validation
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  isValidWallet(wallet) {
    // Ethereum wallet address (0x + 40 hex chars) or ENS name (*.eth)
    if (/^0x[a-fA-F0-9]{40}$/.test(wallet)) return true;
    if (/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.eth$/.test(wallet)) return true;
    return false;
  }

  isPrizeEligible() {
    // Check if player has valid email OR valid wallet for prize eligibility
    const hasEmail = this.playerEmail && this.isValidEmail(this.playerEmail);
    const hasWallet = this.playerWallet && this.isValidWallet(this.playerWallet);
    return hasEmail || hasWallet;
  }

  async showLeaderboard() {
    this.showScreen('leaderboard');
    // Default to "verified" (this week's prizes) if player has email OR wallet, otherwise "all" (overall)
    const defaultFilter = this.isPrizeEligible() ? 'verified' : 'all';
    this.filterLeaderboard(defaultFilter);
  }

  async filterLeaderboard(filter) {
    // Update tab UI
    document.querySelectorAll('.lb-tab').forEach(tab => {
      tab.classList.toggle('active', tab.dataset.filter === filter);
    });

    // Update header text
    const label = document.getElementById('leaderboard-side-label');
    const weekRangeEl = document.getElementById('leaderboard-week-range');
    const tableSection = document.getElementById('leaderboard-table-section');
    const winnersSection = document.getElementById('leaderboard-winners-section');
    const emptyEl = document.getElementById('leaderboard-empty');

    // Show/hide sections based on tab
    if (filter === 'winners') {
      if (label) label.textContent = 'Winners';
      if (weekRangeEl) weekRangeEl.style.display = 'none';
      if (tableSection) tableSection.style.display = 'none';
      if (winnersSection) winnersSection.style.display = 'block';
      if (emptyEl) emptyEl.style.display = 'none';
      this.renderWeeklyWinners();
      return;
    }

    // Score table tabs (This Week / Overall)
    if (tableSection) tableSection.style.display = '';
    if (winnersSection) winnersSection.style.display = 'none';

    if (filter === 'verified') {
      if (label) label.textContent = 'This Week — Prizes';
      // Show current week date range
      const weekStart = getWeekStartMonday();
      const weekEnd = getWeekEndSunday();
      if (weekRangeEl) {
        weekRangeEl.textContent = `${formatShortDate(weekStart)} – ${formatShortDate(weekEnd)}`;
        weekRangeEl.style.display = 'block';
      }
    } else {
      if (label) label.textContent = 'All-time scores';
      if (weekRangeEl) weekRangeEl.style.display = 'none';
    }

    // Fetch ALL scores (limit=500 covers full dataset), cache for 60s
    const now = Date.now();
    const cacheAge = now - (this._leaderboardCacheTime || 0);
    if (!this.cachedLeaderboard || cacheAge >= 60000) {
      this.cachedLeaderboard = await leaderboardManager.getScores('all', false);
      this._leaderboardCacheTime = now;
    }

    let filteredScores = (this.cachedLeaderboard || []).slice();

    // "This Week" tab: keep ONLY scores from the current Monday–Sunday UTC week
    if (filter === 'verified') {
      const weekStart = getWeekStartMonday();
      const weekEnd = getWeekEndSunday();
      filteredScores = filteredScores.filter(score => {
        // Try every possible timestamp field
        const ts = score.created_at || score.timestamp || score.Timestamp || '';
        if (!ts) return false;
        const d = new Date(ts);
        if (isNaN(d.getTime())) return false;
        return d >= weekStart && d <= weekEnd;
      });
    }

    const tbody = document.getElementById('leaderboard-table-body');

    if (!filteredScores || filteredScores.length === 0) {
      if (tbody) tbody.innerHTML = '';
      if (emptyEl) {
        emptyEl.style.display = 'block';
        emptyEl.textContent = filter === 'verified'
          ? 'No scores this week yet. Play a round to claim the top spot!'
          : 'No scores yet. Play a round to claim the top spot.';
      }
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // Sort by score descending (safety net in case API returns unsorted data)
    filteredScores.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));

    // Filter to only show entries with complete data (non-null character_image and player_name)
    const validScores = filteredScores.filter(score =>
      score.player_name && score.character_image
    );

    // Check if we have a recently submitted score to highlight
    const lastScore = this.lastSubmittedScore;

    // Limit to top 10 rows, add side-colored backgrounds
    tbody.innerHTML = validScores.slice(0, 10).map((score, index) => {
      const isTop = index < 3;
      const sideColor = score.side === 'cloak' ? '#5b6170' : '#79a4f9';
      const rank = index + 1;
      // Use leaderboard-specific images with built-in backgrounds (try URL first, then character ID)
      const leaderboardImg = getLeaderboardImage(score.character_image || '', score.character_id || '');

      // Detect and fix column mismatch issue: if score is a string (like "cloak"), columns are wrong
      let displayScore = score.score;
      if (typeof displayScore === 'string' && ['cloak', 'migo'].includes(displayScore)) {
        // Column mismatch detected - score column contains side value
        // Try to use level_reached as fallback or show 0
        displayScore = score.level_reached || 0;
        console.warn('[Leaderboard] Column mismatch detected - score contains side value. Please delete and recreate the Leaderboard sheet in Google Sheets.');
      }

      // Specials used
      const specialsUsed = score.specials_used || 0;

      // Check if this is the player's just-submitted score
      const isPlayerScore = lastScore &&
        score.player_name === lastScore.playerName &&
        score.score === lastScore.score;

      const rowClasses = [
        isTop ? 'leaderboard-top' : '',
        isPlayerScore ? 'leaderboard-highlight' : ''
      ].filter(Boolean).join(' ');

      return `
        <tr class="${rowClasses}" style="background: linear-gradient(to right, ${sideColor}33 0%, transparent 100%);">
          <td><strong>${rank}</strong></td>
          <td>
            <div class="lb-character" style="display: inline-flex; align-items: center; justify-content: center;">
              <img src="${leaderboardImg}" alt="" class="lb-avatar" style="border-radius: 6px;" onerror="this.style.display='none'">
            </div>
          </td>
          <td><strong>${score.player_name || 'Pilot'}</strong></td>
          <td>${specialsUsed}</td>
          <td><strong>${displayScore}</strong></td>
        </tr>
      `;
    }).join('');
  }

  renderWeeklyWinners() {
    const listEl = document.getElementById('leaderboard-winners-list');
    const emptyEl = document.getElementById('leaderboard-winners-empty');

    if (!listEl) return;

    if (WEEKLY_WINNERS.length === 0) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.style.display = 'block';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // Show last 5 winners, most recent first
    const recent = [...WEEKLY_WINNERS].reverse().slice(0, 5);
    const [latest, ...previous] = recent;

    // Featured card for last week's winner (big)
    const latestImg = latest.characterId ? getLeaderboardImage('', latest.characterId) : '';
    const latestSide = latest.side === 'cloak' ? 'Cloaks' : 'Migos';
    const latestDate = `${formatShortDate(latest.weekStart)} – ${formatShortDate(latest.weekEnd)}`;

    let html = `
      <div class="winner-featured gold-border">
        ${latestImg ? `<img src="${latestImg}" alt="" class="winner-featured-avatar" onerror="this.style.display='none'">` : ''}
        <div class="winner-featured-info">
          <div class="winner-featured-label">Last Week's Champion</div>
          <div class="winner-featured-name">${latest.playerName}</div>
          <div class="winner-featured-score">${latest.score.toLocaleString()}</div>
          <div class="winner-featured-details">Week ${latest.weekNumber} · ${latestDate}</div>
          <div class="winner-featured-details">${latest.characterName ? latest.characterName + ' · ' : ''}${latestSide}</div>
        </div>
      </div>
    `;

    // Previous winners (compact: week, name, score)
    if (previous.length > 0) {
      html += `<div class="winner-previous-label">Previous Winners</div>`;
      html += `<div class="winner-previous-scroll">`;
      html += previous.map(winner => {
        const img = winner.characterId ? getLeaderboardImage('', winner.characterId) : '';
        const dateRange = `${formatShortDate(winner.weekStart)} – ${formatShortDate(winner.weekEnd)}`;
        return `
          <div class="winner-compact">
            ${img ? `<img src="${img}" alt="" class="winner-compact-avatar" onerror="this.style.display='none'">` : ''}
            <div class="winner-compact-info">
              <div class="winner-compact-name">${winner.playerName}</div>
              <div class="winner-compact-meta">Wk ${winner.weekNumber} · ${dateRange}</div>
            </div>
            <div class="winner-compact-score">${winner.score.toLocaleString()}</div>
          </div>
        `;
      }).join('');
      html += `</div>`;
    }

    listEl.innerHTML = html;
  }

  toggleMute(buttonId) {
    audioManager.toggleMute();
    this.updateMuteButtons();
  }

  updateMuteButtons() {
    const icon = audioManager.isMuted() ? '🔇' : '🔊';
    const text = audioManager.isMuted() ? 'Sound: OFF' : 'Sound: ON';

    const homeBtn = document.getElementById('btn-mute-home');
    if (homeBtn) homeBtn.textContent = icon;

    const gameBtn = document.getElementById('btn-mute-game');
    if (gameBtn) gameBtn.textContent = icon;

    const menuBtn = document.getElementById('btn-menu-mute');
    if (menuBtn) menuBtn.textContent = text;
  }

  setTheme(theme) {
    // Remove all theme classes
    document.body.classList.remove('theme-cloaks', 'theme-migos');

    // Add new theme class (if not default)
    if (theme === 'cloaks') {
      document.body.classList.add('theme-cloaks');
    } else if (theme === 'migos') {
      document.body.classList.add('theme-migos');
    }

    // Save to localStorage
    localStorage.setItem('beanwars-theme', theme);

    // Update theme button active states
    document.querySelectorAll('.theme-btn').forEach(btn => {
      if (btn.dataset.theme === theme) {
        btn.style.opacity = '1';
        btn.style.fontWeight = '700';
      } else {
        btn.style.opacity = '0.6';
        btn.style.fontWeight = '400';
      }
    });
  }

  loadTheme() {
    const savedTheme = localStorage.getItem('beanwars-theme') || 'default';
    this.setTheme(savedTheme);
  }

  showScreen(screenId) {
    document.querySelectorAll('.screen').forEach(s => s.classList.remove('active'));
    document.getElementById(`screen-${screenId}`)?.classList.add('active');
    this.currentScreen = screenId;

    // Clean up game when returning to home from end screens
    if (screenId === 'home') {
      this.stopGameLoop();
      if (this.gameEngine) {
        this.gameEngine = null;
      }
      if (this.inputManager) {
        this.inputManager = null;
      }
      // Reset selection state
      this.selectedCharacter = null;
      this.selectedSide = null;
      // Clear score highlight for next game
      this.lastSubmittedScore = null;
      // Refresh leaderboard cache for next time
      this.preloadLeaderboard();
    }

  }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new BeanWarsApp();
});
